import { Space, StatusBadge } from '@procurify/ui'

import {
  AmountFraction,
  AmountFractionType,
} from '@webapp/components/AmountFraction'
import { Quantity } from '@webapp/components/Quantity'
import { type BillItemExceptionReceiveQuantity } from '@webapp/domain/accountsPayable/entities'
import { useProcurifyIntl } from '@webapp/hooks'
import { BillItemPopoverLayout } from './BillItemPopoverLayout'
import messages from './messages'

/**
 * The 'BillItemExceptionReceiveQuantityContent' component is responsible for rendering the content of a bill item received quantity exception as the content of a WithPopover component.
 *
 * The exception object comes from the validation errors on the BillItem entity
 */

interface BillItemExceptionReceiveQuantityContentProps {
  exception: BillItemExceptionReceiveQuantity
}

export const BillItemExceptionReceiveQuantityContent = ({
  exception,
}: BillItemExceptionReceiveQuantityContentProps) => {
  const intl = useProcurifyIntl()

  return (
    <BillItemPopoverLayout
      title={intl.formatMessage(messages.receivedTitle)}
      bodyRows={[
        {
          id: 'received-quantity',
          label: intl.formatMessage(messages.receivedLabel),
          valueComponent: (
            <AmountFraction
              type={AmountFractionType.QUANTITY}
              amount={exception.receivedQuantity}
              totalAmount={exception.purchasedQuantity}
            />
          ),
        },
        {
          id: 'previously-billed-quantity',
          label: intl.formatMessage(messages.previouslyBilledLabel),
          valueComponent: (
            <AmountFraction
              type={AmountFractionType.QUANTITY}
              amount={exception.previouslyBilledQuantity}
              totalAmount={exception.purchasedQuantity}
            />
          ),
        },
      ]}
      footerRows={[
        {
          id: 'billable-quantity',
          label: intl.formatMessage(messages.billableLabel),
          valueComponent: (
            <Quantity quantity={exception.billableQuantity} mdp />
          ),
        },
        {
          id: 'billing-quantity',
          label: intl.formatMessage(messages.billingLabel),
          valueComponent: (
            <Space direction='horizontal' align='center'>
              <StatusBadge
                type='error'
                text={intl.formatMessage(messages.statusOverbilledLabel)}
              />
              <Quantity quantity={exception.currentlyBilledQuantity} />
            </Space>
          ),
        },
      ]}
    />
  )
}
